import {
    useState,
    FC,
    useContext,
    useCallback,
    SyntheticEvent,
    useMemo,
    createContext,
} from "react";
import classNames from "classnames";
import { useRouter } from "next/router";
import { FavoriteEventsContext, IGameData, useRuntimeConfig } from "@finbackoffice/site-core";
import { IEventFeedResponse } from "@finbackoffice/websocket-client";
import { formatAppNameText } from "@finbackoffice/fe-core";
import { usePrematchTournament } from "hooks";
import { Svg } from "components/base/svg/Svg";
import { RouteNames } from "utils/constants";
import { InitialDataContext } from "contexts";
import SportIcon from "components/base/sport-icon/SportIcon";
import { GameVariantType } from "../../base/game/Game";
import styles from "../../live-game/game-selector/layouts/categories/categories.module.sass";
import Games from "./Games";

type IWithSocketRequestProps = {
    tournamentId: string;
};

const WithSocketRequest: FC<IWithSocketRequestProps> = ({ tournamentId }) => {
    const { games, loading } = usePrematchTournament(parseInt(tournamentId));
    return <Content games={games} loading={loading} />;
};

type IWithLoadedGamesProps = {
    games: IEventFeedResponse[];
};

const WithLoadedGames: FC<IWithLoadedGamesProps> = ({ games }) => {
    return <Content games={games} />;
};

type IContentProps = IWithLoadedGamesProps & {
    loading?: boolean;
};

const Content: FC<IContentProps> = ({ games, loading }) => {
    const router = useRouter();
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");
    const { selectedGame, variant } = useContext(PrematchGameSelectorContext);
    const [showDropDown, setShowDropDown] = useState(false);

    const { isFav, addFav, removeFav } = useContext(FavoriteEventsContext);

    const handleFavClick = useCallback(
        (event: SyntheticEvent) => {
            event.preventDefault();
            event.stopPropagation();
            if (isFav(selectedGame.id)) {
                removeFav(selectedGame.id);
            } else {
                addFav({
                    id: selectedGame.id,
                    timestamp: selectedGame.scheduled_at
                        ? new Date(selectedGame.scheduled_at).valueOf()
                        : 0,
                    sport_id: selectedGame.sport_id,
                    tournament_id: selectedGame.tournament_id,
                });
            }
        },
        [
            addFav,
            selectedGame.id,
            selectedGame.scheduled_at,
            selectedGame.sport_id,
            selectedGame.tournament_id,
            isFav,
            removeFav,
        ],
    );

    const renderHeader = useMemo(() => {
        const commonContent = (
            <div
                className={styles.tournament}
                onClick={() => Boolean(games?.length) && setShowDropDown(!showDropDown)}>
                <SportIcon sportId={selectedGame.sport_id} />
                <div>
                    <p>
                        <span>{selectedGame.category_name} - </span>
                        {selectedGame.tournament_name}
                    </p>
                    <span>{`${selectedGame.home_team?.name} vs ${selectedGame.away_team?.name}`}</span>
                </div>
            </div>
        );

        switch (variant) {
            case "modal":
                return (
                    <div className={styles.gameHeaderModal}>
                        <Svg
                            src={`/${formatAppNameText(COMMON_SITE_CONFIGS.appName)}/mobile/${
                                isFav(selectedGame.id) ? "star" : "live-game-favorite-unstarred"
                            }.svg`}
                            wrapper="span"
                            className={classNames(styles.favorite, {
                                [styles.favoriteStarred]: isFav(selectedGame.id),
                            })}
                            onClick={handleFavClick}
                        />
                        {commonContent}
                    </div>
                );
            default:
                return (
                    <div className={styles.gameHeader}>
                        <span
                            className={styles.backBtn}
                            onClick={() => {
                                router.push(
                                    `/${RouteNames.SPORT}/${RouteNames.PREMATCH}/${selectedGame.sport_slug}/${selectedGame.category_id}/${selectedGame.tournament_id}`,
                                );
                            }}
                        />
                        {commonContent}
                    </div>
                );
        }
    }, [
        games?.length,
        handleFavClick,
        isFav,
        router,
        selectedGame.away_team?.name,
        selectedGame.category_id,
        selectedGame.category_name,
        selectedGame.home_team?.name,
        selectedGame.id,
        selectedGame.sport_id,
        selectedGame.sport_slug,
        selectedGame.tournament_id,
        selectedGame.tournament_name,
        showDropDown,
        variant,
        COMMON_SITE_CONFIGS.appName,
    ]);

    return (
        <>
            {renderHeader}
            {showDropDown && (
                <Games
                    closeSelector={() => setShowDropDown(false)}
                    gamesData={games}
                    loading={loading || false}
                />
            )}
        </>
    );
};

interface IPrematchGameSelectorContext extends IProps {}

export const PrematchGameSelectorContext = createContext<IPrematchGameSelectorContext>(null as any);

type IProps = {
    selectedGame: IGameData;
    variant: GameVariantType;
};

const PrematchGameSelector: FC<IProps> = ({ selectedGame, variant }) => {
    const router = useRouter();
    const { activeSportGame } = useContext(InitialDataContext);
    const { tournamentId } = router.query;

    const renderContent = useMemo(() => {
        if (activeSportGame.tournamentGames === null) {
            return <WithSocketRequest tournamentId={tournamentId as string} />;
        }

        return <WithLoadedGames games={activeSportGame.tournamentGames} />;
    }, [activeSportGame.tournamentGames, tournamentId]);

    const value = useMemo(
        () => ({
            selectedGame,
            variant,
        }),
        [selectedGame, variant],
    );

    return (
        <PrematchGameSelectorContext.Provider value={value}>
            {renderContent}
        </PrematchGameSelectorContext.Provider>
    );
};

export default PrematchGameSelector;
